import { lazyLoadView } from '@utils/LazyLoad'
import { FEATURES, SEARCH_GROUPS, MODAL } from '@constants/Text'
import { GROUP_WALLET_ROUTE } from '@modules/consolidator/constants/group-wallet'
import {
  buildBackOfficeChildren,
  BACK_OFFICE_DEFAULT_DETAIL_VIEW,
} from '@modules/back-office/utils/build-router/build-router.js'
import { isConsultorBaseAtivos } from '@modules/settings/utils/base-ativos-consultor'

// Guard de carteira.configuracoes ("Configurações do consolidador", alcançada de dentro da
// carteira do cliente) e da sua filha portfolio-modelo. É uma SEGUNDA porta de entrada para a
// MESMA capacidade de settings-v2.asset_allocation.profile (editar Perfil/Portfólio Modelo) —
// precisa da mesma flag de opt-in do consultor (BASE_ATIVOS_CONSULTOR), senão vira bypass do
// gate fechado lá.
//
// Usa `isConsultorBaseAtivos` (e não `podeAcessarBaseAtivos`) porque aqui o gestor é EXCLUÍDO
// de propósito: ele já usa o caminho oficial em Configurações; esta rota existe só como via
// alternativa do consultor com a flag, nunca "gestor OU consultor com flag".
//
// ★ Corrige bug preexistente: o guard antigo era `(user, show) => !user.IS_GESTOR`, mas o
// router invoca `customPermission({ user, show })` — um único objeto. `user` recebia
// `{user, show}` inteiro, `user.IS_GESTOR` era sempre `undefined`, `!undefined` sempre `true`.
// A rota estava aberta a QUALQUER profissional autenticado, gestor incluso, sem checar
// feature nenhuma.
const requireConsolidadorConfig = (ctx) =>
  isConsultorBaseAtivos(ctx.user, ctx.show)

// A curadoria é ato do PROFISSIONAL, não do cliente: os botões de aceitar/recusar GRAVAM em
// `app_dados_ativos_clientes`, e a tela é aberta sobre a carteira de qualquer cliente que o
// profissional atenda — não é a leitura da própria carteira. `can_show` sozinho não
// serve de gate — `src/router/index.js:407-411` resolve `isForUser` como
// `isForType || isForRole || hasPermission`, e `hasPermission` sai do próprio `can_show`, então o
// cliente final passaria com a mesma flag que libera a carteira dele. Só `customPermission` é
// fail-closed. Mesmo racional de `checkApiKeysPermission` em `aawz-intelligence/routes.js`.
const requireSugestoesIa = (ctx) => {
  const user = (ctx && ctx.user) || {}
  const show = (ctx && ctx.show) || {}
  if (user.IS_CLIENTE) return false
  return (
    !!show[FEATURES.CONSOLIDADOR_SUGESTOES_IA] &&
    !!show[FEATURES.ASSET_LOCATION]
  )
}

export default [
  {
    // Drill-through alias used by indicators-card-list goToDesenquadrados.
    // Lands on the consolidator overview (carteira.overview) so the existing
    // Enquadramento filter (FilterOverview.riskFramingSelected) can consume the
    // status_desenquadramento query param and pre-apply.
    path: '/consolidador/carteiras-list',
    name: 'consolidator.carteiras',
    redirect: (to) => ({
      name: 'carteira.overview',
      query: to.query,
    }),
  },
  {
    path: '/carteira',
    redirect: '/consolidador/visao_geral',
  },
  {
    path: '/carteira/visao_geral',
    redirect: '/consolidador/visao_geral',
  },
  {
    path: '/carteira/',
    redirect: '/consolidador/carteiras',
  },
  {
    path: '/consolidador',
    name: 'carteira',
    redirect: '/consolidador/visao_geral',
    meta: {
      search: {
        title: 'Consolidador',
        description: 'Visão consolidada da carteira e posições do cliente',
        keywords: [
          'consolidador',
          'cliente',
          'carteira',
          'portfólio',
          'posição',
        ],
        icon: 'donut',
        group: SEARCH_GROUPS.GESTAO_PORTFOLIOS,
        actions: [
          {
            id: 'cadastrar-ativo',
            title: 'Cadastrar ativo',
            keywords: ['novo ativo', 'adicionar ativo', 'registrar ativo'],
            // Mesmo gatilho do botão "Ativo" do left-bar (modal global).
            modal: MODAL.CONSOLIDADOR_REGISTER_ASSET,
            modalPayload: {},
          },
          {
            id: 'cadastrar-movimentacao',
            title: 'Cadastrar movimentação',
            keywords: [
              'aporte',
              'resgate',
              'nova movimentação',
              'movimentacao',
            ],
            // Espelha openAporteResgate do left-bar: seleciona o cliente e então
            // abre o cadastro de aporte/resgate (ambos modais globais).
            modal: MODAL.SELECIONAR_CLIENTE,
            modalPayload: MODAL.CADASTRAR_APORTE,
          },
        ],
      },
    },
    component: () =>
      lazyLoadView(
        import('@modules/consolidator/views/consolidator-nav-bar.vue')
      ),
    children: [
      {
        path: 'visao_geral',
        name: 'carteira.overview',
        meta: {
          can_show: FEATURES.CONSOLIDATOR_OVERVIEW,
        },
        component: () =>
          lazyLoadView(
            import('@modules/consolidator/views/overview/view-overview.vue')
          ),
      },
      {
        path: 'vencimentos',
        name: 'carteira.vencimentos',
        meta: {
          can_show: FEATURES.MANAGE_REDEMPTION,
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/clients/views/assets-maturity/view-assets-maturity.vue'
            )
          ),
      },
      {
        path: 'configuracoes',
        name: 'carteira.configuracoes',
        component: () =>
          lazyLoadView(
            import(
              '@modules/consolidator/views/settings/ConsolidatorSettingsWrapper.vue'
            )
          ),
        meta: {
          permission: ['profissional'],
          customPermission: requireConsolidadorConfig,
          search: {
            title: 'Configurações do consolidador',
            description: 'Ajustes e asset allocation do consolidador',
            keywords: [
              'configurações',
              'ajustes',
              'asset allocation',
              'consolidador',
            ],
            group: SEARCH_GROUPS.CONFIGURACOES,
          },
        },
        redirect: { name: 'carteira.configuracoes.home' },
        children: [
          {
            path: '',
            name: 'carteira.configuracoes.home',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/consolidator/views/settings/consolidator-settings-home.vue'
                )
              ),
            meta: {
              permission: ['profissional'],
              customPermission: requireConsolidadorConfig,
            },
          },
          {
            path: 'portfolio-modelo',
            name: 'carteira.configuracoes.portfolio-modelo',
            meta: {
              permission: ['profissional'],
              customPermission: requireConsolidadorConfig,
              search: {
                title: 'Portfólio modelo',
                description: 'Configuração de portfólios modelo de alocação',
                keywords: [
                  'portfólio modelo',
                  'alocação',
                  'modelo',
                  'asset allocation',
                ],
                group: SEARCH_GROUPS.CONFIGURACOES,
              },
            },
            component: () =>
              lazyLoadView(
                import('@/src/router/views/settings/portfolio-modelo/index.vue')
              ),
          },
        ],
      },
      {
        // Carteira consolidada por GRUPO FAMILIAR. Espelha as 4 sub-abas da
        // visão por cliente, mas o contexto é o grupo — nunca `currentClient`.
        //
        // `permission: ['profissional']` e não `['all']`: a consolidação de um
        // núcleo familiar inteiro não é conteúdo de tela de cliente final.
        path: 'grupos-familiares',
        name: GROUP_WALLET_ROUTE.SHELL,
        redirect: { name: GROUP_WALLET_ROUTE.CARTEIRA_ATUAL },
        meta: {
          permission: ['profissional'],
          show_current_client: false,
          can_show: FEATURES.FAMILY_GROUP,
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/consolidator/views/grupo-familiar/view-group-wallet.vue'
            )
          ),
        children: [
          {
            path: 'carteira-atual',
            name: GROUP_WALLET_ROUTE.CARTEIRA_ATUAL,
            meta: {
              permission: ['profissional'],
              show_current_client: false,
              can_show: FEATURES.FAMILY_GROUP,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/consolidator/views/grupo-familiar/view-group-carteira-atual.vue'
                )
              ),
          },
          {
            path: 'evolucao',
            name: GROUP_WALLET_ROUTE.EVOLUCAO,
            meta: {
              permission: ['profissional'],
              show_current_client: false,
              can_show: FEATURES.FAMILY_GROUP,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/consolidator/views/grupo-familiar/view-group-evolucao.vue'
                )
              ),
          },
          {
            path: 'analise-portfolio',
            name: GROUP_WALLET_ROUTE.ANALISE,
            meta: {
              permission: ['profissional'],
              show_current_client: false,
              can_show: FEATURES.FAMILY_GROUP,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/consolidator/views/grupo-familiar/view-group-analise.vue'
                )
              ),
          },
          {
            path: 'analise-risco',
            name: GROUP_WALLET_ROUTE.RISCO,
            redirect: { name: GROUP_WALLET_ROUTE.CARTEIRA_ATUAL },
            meta: {
              permission: ['profissional'],
              show_current_client: false,
              can_show: FEATURES.FAMILY_GROUP,
            },
          },
        ],
      },
      {
        path: 'carteiras',
        name: 'carteira.group',
        redirect: 'carteiras/atual',
        meta: {
          permission: ['all'],
          parametrizar: true,
          show_current_client: false,
          can_show: FEATURES.ASSET_LOCATION,
        },
        component: () => lazyLoadView(import('@views/carteira')),
        children: [
          {
            path: 'carteira-atual',
            name: 'carteira.atual',
            meta: {
              parametrizar: true,
              show_current_client: false,
              can_show: FEATURES.ASSET_LOCATION,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/consolidator/views/carteiras/carteira-atual/view-carteira-atual.vue'
                )
              ),
          },
          {
            path: 'atual',
            name: 'carteira.minha',
            meta: {
              parametrizar: true,
              show_current_client: false,
              can_show: FEATURES.ASSET_LOCATION,
              search: {
                title: 'Minha carteira',
                description: 'Carteira consolidada com posições e ativos',
                keywords: ['carteira', 'posição', 'ativos', 'alocação'],
              },
            },
            component: () => lazyLoadView(import('@views/minha-carteira')),
            children: [
              {
                path: 'c/:id_cliente_route',
                name: 'carteira.minha.cliente',
                meta: {
                  parametrizar: true,
                  show_current_client: false,
                  can_show: FEATURES.ASSET_LOCATION,
                },
                component: () => lazyLoadView(import('@views/minha-carteira')),
              },
            ],
          },
          {
            path: 'movimentacoes',
            name: 'carteira.movimentacoes',
            meta: {
              parametrizar: true,
              show_current_client: false,
              can_show: FEATURES.ASSET_LOCATION,
              search: {
                title: 'Movimentações da carteira',
                description: 'Aportes, resgates e movimentações da carteira',
                keywords: ['movimentações', 'aportes', 'resgates', 'extrato'],
              },
            },
            component: () =>
              lazyLoadView(import('@views/carteira-movimentacoes')),
          },
          {
            path: 'ips',
            name: 'carteira.ips',
            // customPermission: exige ASSET_LOCATION e bloqueia quando OCULTAR_CROSS_SELL=TRUE
            meta: {
              show_current_client: false,
              whitelabel_only: true,
              customPermission: ({ show }) =>
                !!show[FEATURES.ASSET_LOCATION] &&
                !show[FEATURES.OCULTAR_CROSS_SELL],
              search: {
                title: 'IPS da carteira',
                description: 'Investment Policy Statement da carteira',
                keywords: ['ips', 'política', 'investimento', 'diretrizes'],
              },
            },
            component: () => lazyLoadView(import('@views/carteira-ips')),
          },
          {
            path: 'analise',
            name: 'carteira.analise',
            meta: {
              show_current_client: false,
              can_show: FEATURES.ASSET_LOCATION,
              search: {
                title: 'Análise da carteira',
                description:
                  'Análise de rentabilidade e desempenho da carteira',
                keywords: ['análise', 'rentabilidade', 'desempenho', 'retorno'],
              },
            },
            component: () => lazyLoadView(import('@views/carteira-analise')),
          },
          {
            path: 'liquidez',
            name: 'carteira.liquidity',
            meta: {
              show_current_client: false,
              can_show: FEATURES.ASSET_LOCATION,
              search: {
                title: 'Liquidez da carteira',
                description: 'Prazos de liquidez e resgate da carteira',
                keywords: ['liquidez', 'resgate', 'prazo', 'disponibilidade'],
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/consolidator/views/carteiras/liquidity/view-liquidity.vue'
                )
              ),
          },
          {
            path: 'sugestoes',
            name: 'carteira.sugestoes',
            meta: {
              show_current_client: false,
              customPermission: requireSugestoesIa,
              search: {
                title: 'Sugestões da IA',
                description:
                  'Fila de sugestões da IA sobre os ativos das carteiras',
                keywords: [
                  'sugestões',
                  'ia',
                  'curadoria',
                  'ativos',
                  'pendentes',
                ],
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/consolidator/views/carteiras/sugestoes/view-sugestoes.vue'
                )
              ),
          },
          {
            path: 'atualizacoes',
            name: 'carteira.atualizacoes',
            meta: {
              parametrizar: true,
              show_current_client: false,
              can_show: FEATURES.ASSET_LOCATION,
              search: {
                title: 'Atualizações da carteira',
                description:
                  'Histórico de atualizações e integrações da carteira',
                keywords: ['atualizações', 'integração', 'status', 'histórico'],
              },
            },
            component: () =>
              lazyLoadView(import('@views/carteira-atualizacoes')),
          },
          {
            path: 'integration-logs',
            name: 'carteira.integrationLogs',
            meta: {
              parametrizar: true,
              show_current_client: false,
              can_show: FEATURES.ASSET_LOCATION,
            },
            component: () =>
              lazyLoadView(import('@views/carteira-integration-logs')),
          },
          {
            path: 'analise_risco',
            name: 'carteira.gestao_risco',
            meta: {
              parametrizar: true,
              show_current_client: false,
              search: {
                title: 'Análise de risco',
                description: 'Gestão e enquadramento de risco da carteira',
                keywords: ['risco', 'enquadramento', 'gestão', 'volatilidade'],
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/consolidator/views/carteiras/analise-risco/view-analise-risco.vue'
                )
              ),
          },
          {
            path: 'atividades',
            name: 'carteira.atividades',
            meta: {
              parametrizar: true,
              show_current_client: false,
              can_show: FEATURES.ASSET_LOCATION,
              search: {
                title: 'Atividades da carteira',
                description: 'Histórico de atividades e eventos da carteira',
                keywords: ['atividades', 'eventos', 'histórico', 'registros'],
              },
            },
            component: () => lazyLoadView(import('@views/carteira-atividades')),
          },
          {
            path: 'evolucao',
            name: 'carteira.evolucao',
            meta: {
              parametrizar: true,
              show_current_client: false,
              can_show: FEATURES.ASSET_LOCATION,
              search: {
                title: 'Evolução da carteira',
                description: 'Evolução histórica do patrimônio da carteira',
                keywords: [
                  'evolução',
                  'histórico',
                  'patrimônio',
                  'crescimento',
                ],
              },
            },
            component: () => lazyLoadView(import('@views/carteira-evolucao')),
          },
          {
            path: 'financeiro',
            name: 'carteira.financeiro',
            meta: {
              permission: ['profissional'],
              parametrizar: true,
              show_current_client: false,
              search: {
                title: 'Financeiro da carteira',
                description: 'Fluxo financeiro e caixa da carteira',
                keywords: ['financeiro', 'fluxo', 'caixa', 'proventos'],
              },
            },
            component: () =>
              lazyLoadView(
                import('@/src/modules/portfolio/views/portfolio-finance')
              ),
          },
        ],
      },
      {
        path: 'back-office',
        name: 'carteira.back_office',
        component: () =>
          lazyLoadView(
            import('@modules/back-office/views/wrapper/view-wrapper.vue')
          ),
        redirect: {
          name: 'carteira.back_office.imports',
          query: { view: BACK_OFFICE_DEFAULT_DETAIL_VIEW },
        },
        children: buildBackOfficeChildren({
          namePrefix: 'carteira.back_office',
          featureFlag: FEATURES.BACK_OFFICE,
          publicTabs: ['imports', 'suggestions'],
        }),
      },
    ],
  },
]
